import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import Orb from "./chat/ChatOrb";

// Example exchanges, written the way the app answers (first name basis, the user's own numbers, a caveat when
// it touches investing). Replace with real outputs from the app when available.
const CONVERSATIONS = [
  {
    chip: "Partir en Espagne ?",
    question: "Je peux me permettre une semaine en Espagne en mai ?",
    answer:
      "Oui, si tu t'y prends maintenant. Il te reste 1 465 € ce mois-ci après tes charges. En mettant 200 € de côté pendant 5 mois, tu finances 1 000 € de voyage sans toucher à ton coussin de sécurité.",
  },
  {
    chip: "8 000 € qui dorment",
    question: "J'ai 8 000 € qui dorment, j'en fais quoi ?",
    answer:
      "D'abord ta sécurité : garde 3 à 6 mois de dépenses, environ 5 000 € pour toi, sur un compte disponible. Les 3 000 € restants peuvent aller vers un placement long terme diversifié, sur 8 à 10 ans. Investir comporte un risque de perte en capital.",
  },
  {
    chip: "Mois plus serré",
    question: "Pourquoi ce mois-ci est plus serré que d'habitude ?",
    answer:
      "Deux raisons : ton assurance auto annuelle (412 €) est tombée le 3, et tes sorties sont à 186 € contre 95 € d'habitude. Sans ça, tu serais dans ta moyenne.",
  },
];

const TYPING_MS = 700;
const AUTOPLAY_MS = 8000;

const Phone = styled.div`
  width: min(340px, 100%);
  height: 600px;
  display: flex;
  flex-direction: column;
  border: 8px solid #141414;
  border-radius: 44px;
  overflow: hidden;
  background: #f4f4f2;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px #2a2a2e;
  font-family: var(--fontR);

  @media screen and (max-width: 48em) {
    height: 560px;
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  padding: 1.25rem 1rem 0.75rem;
  color: #0a0a0b;

  strong {
    font-family: var(--fontL);
    font-size: 1.375rem;
    font-weight: 500;
    letter-spacing: -0.02em;
  }

  span {
    font-size: 0.75rem;
    color: #6b6b70;
  }
`;

const OrbBox = styled.div`
  width: 44px;
  height: 44px;
  margin-bottom: 0.25rem;
`;

const Messages = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.5rem 0.875rem;
  overflow: hidden;
`;

const appear = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;

const Bubble = styled.p`
  max-width: 88%;
  padding: 0.75rem 0.875rem;
  border-radius: 18px;
  font-size: 0.8125rem;
  line-height: 1.5;
  animation: ${appear} 400ms cubic-bezier(0.16, 1, 0.3, 1) both;
  align-self: ${(props) => (props.$user ? "flex-end" : "flex-start")};
  background: ${(props) => (props.$user ? "#131a2a" : "#ffffff")};
  color: ${(props) => (props.$user ? "#fafaf8" : "#0a0a0b")};
  box-shadow: ${(props) => (props.$user ? "none" : "0 1px 2px rgba(10, 10, 11, 0.06)")};
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`;

const Typing = styled.div`
  align-self: flex-start;
  display: flex;
  gap: 4px;
  padding: 0.875rem;
  border-radius: 18px;
  background: #ffffff;

  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #7c5cff;
    animation: ${blink} 1s infinite;
  }

  i:nth-child(2) {
    animation-delay: 0.15s;
  }

  i:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0.75rem 0.875rem 1rem;
  border-top: 1px solid #e4e4de;
  background: #ffffff;
`;

const Chip = styled.button`
  padding: 0.4375rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid ${(props) => (props.$active ? "#7c5cff" : "#e4e4de")};
  background: ${(props) => (props.$active ? "rgba(124, 92, 255, 0.1)" : "#ffffff")};
  color: ${(props) => (props.$active ? "#4b2fd6" : "#0a0a0b")};
  font-family: var(--fontR);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: rgba(124, 92, 255, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #7c5cff;
    outline-offset: 2px;
  }
`;

const ChatDemo = () => {
  const [index, setIndex] = useState(0);
  const [typing, setTyping] = useState(true);
  const [started, setStarted] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const rootRef = useRef(null);

  // Start playing only once the phone is on screen
  useEffect(() => {
    const node = rootRef.current;
    if (!node || !("IntersectionObserver" in window)) {
      setStarted(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStarted(true);
        observer.disconnect();
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    setTyping(true);
    const timer = setTimeout(() => setTyping(false), TYPING_MS);
    return () => clearTimeout(timer);
  }, [index, started]);

  useEffect(() => {
    if (!started || !autoplay) return;
    const timer = setTimeout(() => setIndex((current) => (current + 1) % CONVERSATIONS.length), AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [index, started, autoplay]);

  const pick = (next) => {
    setAutoplay(false);
    setIndex(next);
  };

  const conversation = CONVERSATIONS[index];

  return (
    <Phone ref={rootRef}>
      <Top>
        <OrbBox>
          <Orb forceHoverState={typing && started} />
        </OrbBox>
        <strong>nea</strong>
        <span>Ton assistante financière</span>
      </Top>

      <Messages aria-live="polite">
        {started && (
          <React.Fragment key={index}>
            <Bubble $user>{conversation.question}</Bubble>
            {typing ? (
              <Typing aria-label="nea écrit">
                <i />
                <i />
                <i />
              </Typing>
            ) : (
              <Bubble>{conversation.answer}</Bubble>
            )}
          </React.Fragment>
        )}
      </Messages>

      <Chips>
        {CONVERSATIONS.map((item, itemIndex) => (
          <Chip key={item.chip} type="button" $active={itemIndex === index} onClick={() => pick(itemIndex)}>
            {item.chip}
          </Chip>
        ))}
      </Chips>
    </Phone>
  );
};

export default ChatDemo;
